import { useEffect, useState } from "react";
import { Search, UserPlus, Building2, X, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { MobileSheet } from "./MobileSheet";
import { useIsMobile } from "../hooks/useIsMobile";
import { toast } from "sonner";

const CFG = {
  owner: { base: "owners", idKey: "owner_id", resolveExisting: false },
  instructor: { base: "instructors", idKey: "instructor_id", resolveExisting: true },
  student: { base: "students", idKey: "student_id", resolveExisting: true },
};

const SHEET_TITLE = { owner: "Seleziona proprietario", instructor: "Seleziona istruttore", student: "Seleziona allievo" };

// Reusable Search-or-Create PARTY primitive. Person (all contexts) + Organization (owner only).
// Tenant-safe search, NO AUTOFOCUS, viewport-bounded scroll, mobile bottom sheet.
// value = { person_id?, organization_id?, [idKey]?, label } | null.
export function PersonSearchCreate({ stableId, value, onChange, disabled, context = "owner",
                                     allowCreate = true, entities = ["person"], placeholder = "Cerca persona…" }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const cfg = CFG[context] || CFG.owner;
  const withOrg = entities.includes("organization");

  const selectExisting = async (p) => {
    if (p._kind === "organization") {
      onChange({ organization_id: p.id, owner_id: null, person_id: null, label: p.legal_name || "" });
      setOpen(false);
      return;
    }
    if (context === "instructor") {
      if (p.instructor_id) {
        onChange({ person_id: p.id, instructor_id: p.instructor_id, label: p.display_name || "" });
        setOpen(false);
        return;
      }
      try {
        const { data } = await api.post(`/stables/${stableId}/instructors/resolve-person`, { person_id: p.id });
        onChange({ person_id: p.id, instructor_id: data.id, label: data.name || p.display_name || "" });
        setOpen(false);
      } catch (e) {
        toast.error(e?.response?.status === 403
          ? "Serve il ruolo Gestore per abilitare questa persona come istruttore."
          : (e?.response?.data?.detail || "Errore"));
      }
      return;
    }
    if (context === "student") {
      try {
        const { data } = await api.post(`/stables/${stableId}/students/resolve-person`, { person_id: p.id });
        onChange({ person_id: p.id, student_id: data.id, label: data.name || p.display_name || "",
          habitual_horse_id: data.habitual_horse_id || null, habitual_horse_name: data.habitual_horse_name || null });
        setOpen(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore");
      }
      return;
    }
    onChange({ person_id: p.id, owner_id: null, label: p.display_name || "" });
    setOpen(false);
  };

  const created = (r) => { onChange(r); setOpen(false); };

  const trigger = (
    <button type="button" data-testid="person-picker-trigger" disabled={disabled} onClick={() => setOpen(true)}
      className="w-full flex items-center justify-between gap-2 rounded-md border border-[#E5DEC9] bg-white px-3 h-10 text-sm text-left disabled:opacity-50 hover:border-[#8C532B]/50 transition-colors">
      <span className={value?.label ? "text-[#1B3B2B] truncate" : "text-[#9AA39C]"}>{value?.label || placeholder}</span>
      {value?.label ? (
        <X data-testid="person-picker-clear" onClick={(e) => { e.stopPropagation(); onChange(null); }}
          className="h-4 w-4 text-[#9AA39C] shrink-0 hover:text-[#1B3B2B]" />
      ) : (<Search className="h-4 w-4 text-[#9AA39C] shrink-0" />)}
    </button>
  );

  const panel = (
    <PickerPanel stableId={stableId} base={cfg.base} allowCreate={allowCreate} withOrg={withOrg}
      onSelect={selectExisting} onCreated={created} />
  );

  if (isMobile) {
    return (<>
      {trigger}
      <MobileSheet open={open} onOpenChange={setOpen} title={SHEET_TITLE[context] || "Seleziona"} testId="person-picker-sheet">
        <div className="flex flex-col min-h-0 flex-1">{open && panel}</div>
      </MobileSheet>
    </>);
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" collisionPadding={12}
        className="w-80 max-w-[90vw] p-0 flex flex-col overflow-hidden max-h-[min(30rem,var(--radix-popover-content-available-height))]"
        onOpenAutoFocus={(e) => e.preventDefault()} data-testid="person-picker-popover">
        {open && panel}
      </PopoverContent>
    </Popover>
  );
}

function PickerPanel({ stableId, base, allowCreate, withOrg, onSelect, onCreated }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("search");

  useEffect(() => {
    if (mode !== "search") return;
    const s = q.trim();
    if (s.length < 2) { setResults([]); setLoading(false); return; }
    let active = true; setLoading(true);
    const t = setTimeout(async () => {
      try {
        const calls = [api.get(`/stables/${stableId}/persons/search`, { params: { q: s } })];
        if (withOrg) calls.push(api.get(`/stables/${stableId}/organizations/search`, { params: { q: s } }));
        const [pr, or] = await Promise.all(calls);
        const persons = (pr.data || []).map((p) => ({ ...p, _kind: "person" }));
        const orgs = withOrg ? (or?.data || []).map((o) => ({ ...o, _kind: "organization" })) : [];
        if (active) setResults([...persons, ...orgs]);
      } catch { if (active) setResults([]); }
      finally { if (active) setLoading(false); }
    }, 220);
    return () => { active = false; clearTimeout(t); };
  }, [q, mode, stableId, withOrg]);

  if (mode === "create-person") {
    return <CreatePersonForm stableId={stableId} base={base} initialName={q} onBack={() => setMode("search")}
      onCreated={(r) => onCreated({ person_id: r.person_id, owner_id: r.owner_id, instructor_id: r.instructor_id, student_id: r.student_id, label: r.display_name || r.name || "" })}
      onSelect={onSelect} />;
  }
  if (mode === "create-org") {
    return <CreateOrgForm stableId={stableId} initialName={q} onBack={() => setMode("search")}
      onCreated={(r) => onCreated({ organization_id: r.organization_id, owner_id: r.owner_id, label: r.display_name || "" })} />;
  }

  const short = q.trim().length < 2;
  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="person-picker-panel">
      <div className="p-2 border-b border-[#E5DEC9] shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA39C]" />
          <Input data-testid="person-picker-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={withOrg ? "Persona o organizzazione…" : "Nome, email, telefono, CF…"} className="pl-8" autoFocus={false} autoComplete="off" />
        </div>
      </div>
      <div className="max-h-64 min-h-0 overflow-auto p-1 flex-1" data-testid="person-picker-results">
        {loading && <div className="px-3 py-3 text-sm text-[#9AA39C] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca…</div>}
        {!loading && short && <div className="px-3 py-3 text-xs text-[#9AA39C]">Digita almeno 2 caratteri per cercare.</div>}
        {!loading && !short && results.length === 0 && <div className="px-3 py-3 text-sm text-[#9AA39C]">Nessun risultato in questa scuderia.</div>}
        {(() => {
          const counts = {};
          results.forEach((p) => { if (p._kind === "person") { const k = (p.display_name || "").trim().toLowerCase(); if (k) counts[k] = (counts[k] || 0) + 1; } });
          return results.map((p) => {
            if (p._kind === "organization") {
              return (
                <button key={`o-${p.id}`} type="button" data-testid={`person-picker-org-${p.id}`} onClick={() => onSelect(p)}
                  className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F0EAE1] transition-colors flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#8C532B] shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-[#1B3B2B] truncate">{p.legal_name || "Organizzazione"}</span>
                    <span className="block text-[10px] text-[#9AA39C]">Organizzazione</span>
                  </span>
                </button>
              );
            }
            const dup = counts[(p.display_name || "").trim().toLowerCase()] > 1;
            const hint = dup ? (p.email || p.phone || p.codice_fiscale) : null;
            return (
              <button key={`p-${p.id}`} type="button" data-testid={`person-picker-opt-${p.id}`} onClick={() => onSelect(p)}
                className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F0EAE1] transition-colors">
                <span className="block text-sm text-[#1B3B2B] truncate">{p.display_name || "Senza nome"}</span>
                {hint && <span className="block text-[10px] text-[#9AA39C] truncate">{hint}</span>}
              </button>
            );
          });
        })()}
      </div>
      {allowCreate && (
        <div className="p-2 border-t border-[#E5DEC9] shrink-0 space-y-1.5">
          <Button type="button" variant="outline" data-testid="person-picker-create-btn" className="w-full justify-start"
            onClick={() => setMode("create-person")}>
            <UserPlus className="h-4 w-4 mr-2" /> Crea nuova persona
          </Button>
          {withOrg && (
            <Button type="button" variant="outline" data-testid="org-picker-create-btn" className="w-full justify-start"
              onClick={() => setMode("create-org")}>
              <Building2 className="h-4 w-4 mr-2" /> Crea nuova organizzazione
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePersonForm({ stableId, base, initialName, onBack, onCreated, onSelect }) {
  const [form, setForm] = useState(() => {
    const parts = (initialName || "").trim().split(/\s+/).filter(Boolean);
    return { first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "", email: "", phone: "", codice_fiscale: "" };
  });
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/${base}/person`, form);
      if (data?.cf_duplicate_warning) toast.warning("Un'altra persona in questa scuderia ha lo stesso codice fiscale.");
      toast.success("Persona creata"); onCreated(data);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Serve il ruolo Gestore per creare una persona qui." : (e?.response?.data?.detail || "Errore"));
    } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) { toast.error("Inserisci nome o cognome"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/persons/duplicate-check`, {
        first_name: form.first_name, last_name: form.last_name, codice_fiscale: form.codice_fiscale });
      const c = data?.candidates || [];
      if (c.length > 0) { setCandidates(c); setBusy(false); return; }
      await doCreate();
    } catch { await doCreate(); }
  };
  const restorePerson = async (c) => {
    try {
      await api.post(`/stables/${stableId}/persons/${c.id}/restore`);
      toast.success("Persona ripristinata");
      onSelect({ ...c, _kind: "person" });
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Serve il ruolo Gestore per ripristinare." : (e?.response?.data?.detail || "Errore"));
    }
  };
  const fields = [["first_name", "Nome"], ["last_name", "Cognome"], ["email", "Email (opzionale)"], ["phone", "Telefono (opzionale)"], ["codice_fiscale", "Codice fiscale (opzionale)"]];
  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="person-picker-create-panel">
      <div className="p-2 border-b border-[#E5DEC9] shrink-0 flex items-center gap-2">
        <button type="button" onClick={onBack} data-testid="person-picker-create-back" className="text-[#5F6B64] hover:text-[#1B3B2B]"><ArrowLeft className="h-4 w-4" /></button>
        <span className="text-sm font-medium text-[#1B3B2B]">Crea nuova persona</span>
      </div>
      <div className="p-3 space-y-3 overflow-auto flex-1 min-h-0">
        {candidates && candidates.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="person-picker-dup-warning">
            <div className="flex items-start gap-2 text-sm text-amber-800"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{candidates.some((c) => c.archived) ? "Esiste già una persona archiviata con questi dati." : "Potrebbe esistere già una persona corrispondente."}</span></div>
            <div className="space-y-1.5">
              {candidates.map((c) => (
                <div key={c.id} className="rounded-md bg-white border border-amber-200 px-2.5 py-2">
                  <span className="min-w-0"><span className="block text-sm text-[#1B3B2B] truncate">{c.display_name || "Senza nome"}{c.archived && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded border border-[#9AA39C] text-[#5F6B64]">Archiviata</span>}</span>
                    <span className="text-[10px] text-[#9AA39C]">{(c.reasons || []).map((r) => (r === "codice_fiscale" ? "stesso CF" : "stesso nome")).join(" · ")}</span></span>
                  <div className="mt-1.5 flex gap-1.5">
                    {c.archived ? (<>
                      <Button type="button" size="sm" variant="outline" data-testid={`person-picker-view-${c.id}`} className="h-7 text-xs" onClick={() => window.open(`/persone/${c.id}`, "_blank")}>Visualizza</Button>
                      <Button type="button" size="sm" variant="outline" data-testid={`person-picker-restore-${c.id}`} className="h-7 text-xs" onClick={() => restorePerson(c)}>Ripristina</Button>
                    </>) : (
                      <Button type="button" size="sm" variant="outline" data-testid={`person-picker-use-${c.id}`} className="h-7 text-xs" onClick={() => onSelect({ ...c, _kind: "person" })}>Usa questa</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {fields.map(([k, lbl]) => (
          <div key={k} className="space-y-1"><Label htmlFor={`np-${k}`} className="text-xs text-[#5F6B64]">{lbl}</Label>
            <Input id={`np-${k}`} data-testid={`person-picker-field-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} autoFocus={false} autoComplete="off" /></div>
        ))}
      </div>
      <div className="p-2 border-t border-[#E5DEC9] shrink-0 flex gap-2">
        {candidates && candidates.length > 0 ? (
          <Button type="button" className="flex-1 bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy} data-testid="person-picker-create-anyway" onClick={doCreate}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea comunque</Button>
        ) : (
          <Button type="button" className="flex-1 bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy} data-testid="person-picker-create-submit" onClick={submit}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea e seleziona</Button>
        )}
      </div>
    </div>
  );
}

function CreateOrgForm({ stableId, initialName, onBack, onCreated }) {
  const [form, setForm] = useState({ legal_name: initialName || "", partita_iva: "", codice_fiscale: "", email: "", phone: "", pec: "", organization_type: "" });
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const doCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/owners/organization`, form);
      if (data?.piva_duplicate_warning) toast.warning("Un'altra organizzazione ha la stessa Partita IVA.");
      toast.success("Organizzazione creata"); onCreated(data);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Serve il ruolo Gestore." : (e?.response?.data?.detail || "Errore"));
    } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!form.legal_name.trim()) { toast.error("Inserisci la ragione sociale"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/stables/${stableId}/organizations/duplicate-check`, {
        legal_name: form.legal_name, partita_iva: form.partita_iva, codice_fiscale: form.codice_fiscale });
      const c = data?.candidates || [];
      if (c.length > 0) { setCandidates(c); setBusy(false); return; }
      await doCreate();
    } catch { await doCreate(); }
  };
  const selectOrg = (c) => onCreated({ organization_id: c.id, owner_id: null, person_id: null, label: c.legal_name || "" });
  const restoreOrg = async (c) => {
    try {
      await api.post(`/stables/${stableId}/organizations/${c.id}/restore`);
      toast.success("Organizzazione ripristinata"); selectOrg(c);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Serve il ruolo Gestore per ripristinare." : (e?.response?.data?.detail || "Errore"));
    }
  };
  const fields = [["legal_name", "Ragione sociale *"], ["organization_type", "Tipo (SSD, ASD, Azienda…) (opz.)"], ["partita_iva", "Partita IVA (opz.)"], ["codice_fiscale", "Codice fiscale (opz.)"], ["email", "Email (opz.)"], ["pec", "PEC (opz.)"], ["phone", "Telefono (opz.)"]];
  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="org-picker-create-panel">
      <div className="p-2 border-b border-[#E5DEC9] shrink-0 flex items-center gap-2">
        <button type="button" onClick={onBack} data-testid="org-picker-create-back" className="text-[#5F6B64] hover:text-[#1B3B2B]"><ArrowLeft className="h-4 w-4" /></button>
        <span className="text-sm font-medium text-[#1B3B2B]">Crea nuova organizzazione</span>
      </div>
      <div className="p-3 space-y-3 overflow-auto flex-1 min-h-0">
        {candidates && candidates.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="org-picker-dup-warning">
            <div className="flex items-start gap-2 text-sm text-amber-800"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{candidates.some((c) => c.archived) ? "Esiste già un'organizzazione archiviata con questi dati." : "Potrebbe esistere già un'organizzazione corrispondente."}</span></div>
            <div className="space-y-1.5">
              {candidates.map((c) => (
                <div key={c.id} className="rounded-md bg-white border border-amber-200 px-2.5 py-2">
                  <span className="min-w-0"><span className="block text-sm text-[#1B3B2B] truncate">{c.legal_name || "Senza nome"}{c.archived && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded border border-[#9AA39C] text-[#5F6B64]">Archiviata</span>}</span>
                    <span className="text-[10px] text-[#9AA39C]">{(c.reasons || []).map((r) => r === "partita_iva" ? "stessa P.IVA" : r === "codice_fiscale" ? "stesso CF" : "stessa ragione sociale").join(" · ")}</span></span>
                  <div className="mt-1.5 flex gap-1.5">
                    <Button type="button" size="sm" variant="outline" data-testid={`org-picker-view-${c.id}`} className="h-7 text-xs" onClick={() => window.open(`/organizzazioni/${c.id}`, "_blank")}>Visualizza</Button>
                    {c.archived ? (
                      <Button type="button" size="sm" variant="outline" data-testid={`org-picker-restore-${c.id}`} className="h-7 text-xs" onClick={() => restoreOrg(c)}>Ripristina</Button>
                    ) : (
                      <Button type="button" size="sm" variant="outline" data-testid={`org-picker-use-${c.id}`} className="h-7 text-xs" onClick={() => selectOrg(c)}>Usa questa</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {fields.map(([k, lbl]) => (
          <div key={k} className="space-y-1"><Label htmlFor={`no-${k}`} className="text-xs text-[#5F6B64]">{lbl}</Label>
            <Input id={`no-${k}`} data-testid={`org-picker-field-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} autoFocus={false} autoComplete="off" /></div>
        ))}
      </div>
      <div className="p-2 border-t border-[#E5DEC9] shrink-0">
        {candidates && candidates.length > 0 ? (
          <Button type="button" className="w-full bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy} data-testid="org-picker-create-anyway" onClick={doCreate}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea comunque</Button>
        ) : (
          <Button type="button" className="w-full bg-[#1B3B2B] hover:bg-[#142d21]" disabled={busy} data-testid="org-picker-create-submit" onClick={submit}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea e seleziona</Button>
        )}
      </div>
    </div>
  );
}
